import type { StagedEntry } from '@/lib/api';

/** Choosing how to preview one entry.
 *
 *  Preview is a dispatch, not one converter: most of what arrives is not an
 *  office document, and the branch that needs no library at all - a script's own
 *  source - is among the most useful. */

/** How an entry should be rendered, decided from what inspection found. */
export type PreviewKind =
  /** PDF: rasterised to page images, so the lure is seen as laid out. Its own
   *  branch because it renders rather than converts, and a scan has pixels but
   *  no text at all. */
  | 'pdf'
  /** Office, ODF, RTF, EPUB or CSV: converted to Markdown by the wasm
   *  converter. */
  | 'document'
  /** Already text: shown as its own source. */
  | 'text'
  /** A Windows shortcut: its target, arguments and working directory. */
  | 'shortcut'
  /** A PE: the static profile answers this, not a preview pane. */
  | 'binary'
  /** A container: the entry tree is the preview. */
  | 'container'
  /** Nothing useful to show. */
  | 'none';

/** Formats the converter turns into Markdown, as `inspect` names them. Kept in
 *  step with what src/docpreview builds, or an entry reaches a converter that
 *  will refuse it. */
const DOCUMENT_FORMATS = new Set(['OOXML', 'CFBF', 'RTF', 'ODF', 'EPUB']);

/** Extensions the converter reads that carry no signature to detect them by.
 *  A spreadsheet delimited by commas is bytes that look like text, so only the
 *  name says it is a table - and read as plain text it is a wall of commas
 *  rather than the rows a lure was laid out in. */
const DOCUMENT_EXTENSIONS = new Set(['csv', 'tsv']);

/** Extensions whose content is worth showing as source. A display hint only:
 *  what gets detonated is the server's call, from the file's own bytes. */
const SCRIPT_EXTENSIONS = new Set([
  'js',
  'jse',
  'vbs',
  'vbe',
  'wsf',
  'ps1',
  'psm1',
  'hta',
  'bat',
  'cmd',
  'sh',
  'py',
  'json',
  'xml',
  'html',
  'htm',
  'svg',
  'txt',
  'md',
  'ini',
  'cfg',
  'yaml',
  'yml',
]);

/** The lowercase extension of a display name, without its dot. A leading dot is
 *  a name rather than an extension (".bashrc"), as the server also treats it. */
export function extensionOf(name: string): string {
  const i = name.lastIndexOf('.');
  if (i <= 0 || i === name.length - 1) return '';
  return name.slice(i + 1).toLowerCase();
}

/** Decide how to preview an entry.
 *
 *  Format first, name second: a document renamed `.pdf` is ordinary dropper
 *  practice, so dispatching on the name would pick the wrong renderer. The
 *  extension decides only where the bytes carry no signature. */
export function previewKindOf(entry: StagedEntry): PreviewKind {
  if (!entry.selectable) return 'none';

  const format = entry.detected_format ?? '';
  if (format === 'PDF') return 'pdf';
  if (DOCUMENT_FORMATS.has(format)) return 'document';
  if (format === 'LNK') return 'shortcut';
  if (entry.pe_kind) return 'binary';
  if (entry.is_container) return 'container';

  // No signature matched. Text formats carry none, and being wrong costs a
  // pane that says "not text", not a bad run.
  const ext = extensionOf(entry.name);
  if (DOCUMENT_EXTENSIONS.has(ext)) return 'document';
  if (SCRIPT_EXTENSIONS.has(ext)) return 'text';
  return 'none';
}

/** Whether a kind has a preview to show at all.
 *
 *  `binary` and `container` are answers, not previews: a PE's structure is the
 *  static profile's to state and an archive's contents are the entry tree's. A
 *  section that renders only its own excuse is worse than no section - it takes
 *  the room a preview would have, and reads as something that failed rather
 *  than something that was never applicable. Callers hide the section instead. */
export function canPreview(kind: PreviewKind): boolean {
  return kind === 'pdf' || kind === 'document' || kind === 'text' || kind === 'shortcut';
}

/** How the `doc` job's container names map onto the renderers.
 *
 *  A second table onto one renderer, not a second renderer: the doc job names
 *  containers as ftguess does and `inspect` names them from its own signatures,
 *  so the two spell the same thing differently (`OpenXML`/`OOXML`,
 *  `OLE`/`CFBF`). Mapping both onto `PreviewKind` keeps the submit page and the
 *  report showing a document the same way.
 *
 *  The text forms are what Office opens with no magic number to find them by -
 *  Word 2003 XML, MHTML - and reading them as source is the point. */
const DOC_CONTAINERS: Record<string, PreviewKind> = {
  PDF: 'pdf',
  OLE: 'document',
  OpenXML: 'document',
  RTF: 'document',
  XML: 'text',
  MIME: 'text',
  SYLK: 'text',
};

/** Decide how to preview an analysed sample from the container the `doc` job
 *  identified it as. Unknown containers render nothing rather than guessing -
 *  the pane's other sections still carry the analysis. */
export function previewKindOfContainer(container: string): PreviewKind {
  return DOC_CONTAINERS[container] ?? 'none';
}

